<template>
  <section class="rebook-search page-wrapper">
    <h2 class="rebook-search__title title-large">
      {{ $t('afterSale.rebook.search.title') }}
    </h2>
    <section class="search-container">
      <SearchOrigin
        :disabled="disabled"
        :initial-station="currentOrigin"
      />
    </section>
    <section class="search-container">
      <DirectionButton :disabled="disabled" />
      <SearchDestination
        :disabled="disabled"
        :initial-station="currentDestination"
      />
    </section>
    <div class="rebook-search__dates">
      <div class="rebook-search__date">
        <TravelDate
          v-if="direction !== 'inbound'"
          :disabled="disabled"
          :initial-date="currentDepartureDate"
        />
      </div>
      <div class="rebook-search__date">
        <ReturnDate
          v-if="isEntireJourney || direction === 'inbound'"
          :disabled="disabled"
          :initial-date="currentReturnDate"
          class="rebook-search__return-date"
        />
      </div>
    </div>
    <RebookSearchButton
      :disabled="disabled || !canProceed"
      :booking-number="bookingNumber"
      :items-to-replace="itemsToReplace"
      :direction="direction"
      @close="$emit('search')"
    />
  </section>
</template>

<script lang="ts">
  import { defineComponent, computed, onMounted, watch, PropType } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useRoute } from 'vue-router'
  import { Station } from '@/types/stationTypes'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/Traveldate/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'
  import RebookSearchButton from './RebookSearchButton.vue'

  export default defineComponent({
    name: 'RebookSearch',
    components: {
      SearchOrigin,
      SearchDestination,
      TravelDate,
      ReturnDate,
      DirectionButton,
      RebookSearchButton,
    },
    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound' | 'entire'>,
        required: true,
        validator: (value: 'outbound' | 'inbound' | 'entire') =>
          ['outbound', 'inbound', 'entire'].includes(value),
      },
      bookingNumber: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const route = useRoute()
      const { t } = useI18n()

      onMounted(() => {
        const [origin, destination, departureDate, returnDate] = [
          currentOrigin.value,
          currentDestination.value,
          currentDepartureDate.value,
          currentReturnDate.value,
        ]

        if (origin) {
          journeyStore.OriginStation = createFullStation(origin)
        }
        if (destination) {
          journeyStore.setDestinationStation(createFullStation(destination))
        }
        if (departureDate) {
          journeyStore.setDepartureDate(departureDate)
        }
        if (returnDate) {
          journeyStore.setReturnDate(returnDate)
        }
      })

      const isEntireJourney = computed(() => props.direction === 'entire')

      const currentSegments = computed(() => {
        return props.direction === 'inbound'
          ? bookingStore.bookingData?.inbound_booking_tariff_segments || []
          : bookingStore.bookingData?.outbound_booking_tariff_segments || []
      })

      const journeyEndpoints = computed(() => {
        if (!currentSegments.value?.length) return null

        const outboundDepartureDateTime =
          currentSegments.value[0]?.booking_journey_segments?.[0]
            ?.departure_date_time

        const inboundDepartureDateTime =
          props.direction === 'entire' &&
          bookingStore.bookingData?.inbound_booking_tariff_segments?.[0]
            ?.booking_journey_segments?.[0]
            ? bookingStore.bookingData.inbound_booking_tariff_segments[0]
                .booking_journey_segments[0].departure_date_time
            : undefined

        return {
          origin: currentSegments.value[0].departure_station,
          destination:
            currentSegments.value[currentSegments.value.length - 1]
              .arrival_station,
          departureDate: outboundDepartureDateTime,
          returnDate: inboundDepartureDateTime,
        }
      })

      const currentOrigin = computed(() => {
        if (!journeyEndpoints.value?.origin) return undefined
        return {
          _u_i_c_station_code:
            journeyEndpoints.value.origin._u_i_c_station_code,
          name: journeyEndpoints.value.origin.name,
          short_code: journeyEndpoints.value.origin.short_code,
          country_code: journeyEndpoints.value.origin.country_code,
        }
      })

      const currentDestination = computed(() => {
        if (!journeyEndpoints.value?.destination) {
          return undefined
        }

        const result = {
          _u_i_c_station_code:
            journeyEndpoints.value.destination._u_i_c_station_code,
          name: journeyEndpoints.value.destination.name,
          short_code: journeyEndpoints.value.destination.short_code,
          country_code: journeyEndpoints.value.destination.country_code,
        }

        return result
      })

      const currentDepartureDate = computed(() => {
        if (journeyStore.departureDate) {
          const dateStr = journeyStore.departureDate
          let result
          if (dateStr.includes('T')) {
            result = dateStr
          } else {
            const date = new Date(`${dateStr}T12:00:00+02:00`)
            result = date.toISOString()
          }
          return result
        }

        if (!journeyEndpoints.value?.departureDate) {
          return undefined
        }
        const dateStr = journeyEndpoints.value.departureDate

        let result
        if (dateStr.includes('T')) {
          result = dateStr
        } else {
          const date = new Date(`${dateStr}T12:00:00+02:00`)
          result = date.toISOString()
        }
        return result
      })

      const currentReturnDate = computed(() => {        
        if (!isEntireJourney.value) {
          return undefined
        }

        if (journeyStore.returnDate) {
          const dateStr = journeyStore.returnDate
          let result
          if (dateStr.includes('T')) {
            result = dateStr
          } else {
            const date = new Date(`${dateStr}T12:00:00+02:00`)
            result = date.toISOString()
          }
          return result
        }

        if (!journeyEndpoints.value?.returnDate) {
          return undefined
        }
        const dateStr = journeyEndpoints.value.returnDate

        let result
        if (dateStr.includes('T')) {
          result = dateStr
        } else {
          const date = new Date(`${dateStr}T12:00:00+02:00`)
          result = date.toISOString()
        }
        return result
      })

      const createFullStation = (partialStation: Partial<Station>): Station => {
        return {
          name: partialStation.name || '',
          _u_i_c_station_code: partialStation._u_i_c_station_code || '',
          short_code: partialStation.short_code || '',
          country_code: partialStation.country_code || 'NO',

          ...(partialStation.timezone && { timezone: partialStation.timezone }),
          ...(partialStation.meta_data && {
            meta_data: partialStation.meta_data,
          }),
          ...(partialStation.connected_stations && {
            connected_stations: partialStation.connected_stations,
          }),
          ...(partialStation.synonyms && { synonyms: partialStation.synonyms }),
          ...(partialStation.stop_exceptions && {
            stop_exceptions: partialStation.stop_exceptions,
          }),
          ...(partialStation.departure_timestamp && {
            departure_timestamp: partialStation.departure_timestamp,
          }),
          ...(partialStation.arrival_timestamp && {
            arrival_timestamp: partialStation.arrival_timestamp,
          }),
        }
      }

      const canProceed = computed(() => {
        const hasValidDates = isEntireJourney.value
          ? journeyStore.departureDate && journeyStore.returnDate
          : journeyStore.departureDate

        const hasValidStations =
          journeyStore.OriginStation?._u_i_c_station_code &&
          journeyStore.destinationStation?._u_i_c_station_code

        return hasValidStations && hasValidDates
      })

      const isPassengerCancelled = (passengerId: string) => {
        return bookingStore.bookingData?.passengers?.some(
          (p) => p.id === passengerId && p.cancel_timestamp
        )
      }

      const itemsToReplace = computed(() => {
        if (!bookingStore.bookingData) {
          return []
        }

        const getProductUuidsFromSegment = (segment: any) => {
          const result: string[] = []

          interface RequiredProduct {
            uuid: string
            can_be_rebooked: boolean
            cancelled: boolean
            passenger_id: string
          }

          if (segment?.required_products?.length) {
            const requiredProductUuids = segment.required_products
              .filter(
                (product: RequiredProduct) =>
                  product?.uuid &&
                  product.can_be_rebooked &&
                  !product.cancelled &&
                  !isPassengerCancelled(product.passenger_id)
              )
              .map((product: RequiredProduct) => product.uuid)

            result.push(...requiredProductUuids)
          }

          return result
        }

        if (props.direction === 'entire') {
          const outboundSegments =
            bookingStore.bookingData.outbound_booking_tariff_segments || []
          const inboundSegments =
            bookingStore.bookingData.inbound_booking_tariff_segments || []

          const outboundItems = outboundSegments.reduce(
            (acc: string[], segment) => {
              const segmentUuids = getProductUuidsFromSegment(segment)
              acc.push(...segmentUuids)
              return acc
            },
            [] as string[]
          )

          const inboundItems = inboundSegments.reduce(
            (acc: string[], segment) => {
              const segmentUuids = getProductUuidsFromSegment(segment)
              acc.push(...segmentUuids)
              return acc
            },
            [] as string[]
          )

          return [...outboundItems, ...inboundItems]
        } else {
          const segments =
            props.direction === 'inbound'
              ? bookingStore.bookingData.inbound_booking_tariff_segments
              : bookingStore.bookingData.outbound_booking_tariff_segments

          if (!segments?.length) return []

          const result = segments.reduce((acc: string[], segment) => {
            const segmentUuids = getProductUuidsFromSegment(segment)
            acc.push(...segmentUuids)
            return acc
          }, [] as string[])

          return result
        }
      })

      return {
        isEntireJourney,
        currentOrigin,
        currentDestination,
        currentDepartureDate,
        currentReturnDate,
        canProceed,
        itemsToReplace,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .rebook-search {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: rem(16) 16px;
    box-sizing: border-box;

    &__title {
      margin-top: rem(24);
      text-align: center;
    }

    .search-container {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .direction-button {
      top: -37%;
      transform: translateX(-50%) rotate(-90deg);
    }

    &__dates {
      display: flex;
      gap: 16px;
    }
  }
</style>
